import { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';
import store from '@/store';
import { CustomRouteMeta, RouteNames } from '@/data/routes';
import { ProfileUserRouteMessageCode } from '@/utils/route-message-codes';
/**
 * Checks if user has access to a route based on feature flags
 */
function checkFeatureFlags(requiredFlags: string[]): boolean {
  if (!requiredFlags?.length) return true;

  const { featureFlags, globalFeatureFlags } = store?.getters || {};

  return requiredFlags.every((flag) => {
    return featureFlags?.[flag] === true || globalFeatureFlags?.[flag] === true;
  });
}

/**
 * Navigation guard that processes the 'guard' property in route meta
 * This works with your existing route structure
 */
export function customRouteGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
) {
  const meta = to.meta as CustomRouteMeta | undefined;

  if (!meta) {
    return next();
  }

  const fallback = meta.fallbackRoute || RouteNames.HOME.INITIAL;

  if (meta.featureFlags?.length && !checkFeatureFlags(meta.featureFlags)) {
    console.warn(
      `Access denied: Missing required feature flags for ${String(to.name)}`,
    );
    return next({ name: fallback });
  }

  if (meta.hasAccess) {
    try {
      if (!meta.hasAccess({ store, to })) {
        console.warn(
          `Access denied: Custom access check failed for ${String(to.name)}`,
        );
        return next({ name: fallback });
      }
    } catch (error) {
      console.warn(
        `hasAccess function threw an error for ${String(to.name)}, allowing access: ${error}`,
      );
      // Continue to allow access
    }
  }

  return next();
}

export function canEditUserGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
) {
  const canEditUser =
    !store.getters.businessHasPendingBillContractOrActiveAdvance;
  if (!canEditUser) {
    return next({
      name: RouteNames.HOME.INITIAL,
      query: { warn: ProfileUserRouteMessageCode.CannotUpdateUserDetails },
    });
  }
  next();
}
